import { Injectable } from '@angular/core';
import { pick } from '@sama/micro-dash';
import {
  AbstractContentUndoService2D,
  UndoState2D,
} from '../../shared/state/abstract-content-state-2d-undo.service';
import { Selection } from '../../shared/state/abstract-sensor-content-state-2d';
import { ImageContentState } from './image-content-state';
import { ImageContentStore } from './image-content-store';

interface ImageUndoState extends UndoState2D {
  rawMask?: Uint8Array;
  selection: Selection;
}

const maxMemory = 50000000; // 50

/**
 * ImageContentStateUndoService is responsible for updating the ImageContentState
 * after an undo or a redo and setting that state to the ImageContentStore
 */
@Injectable()
export class ImageContentStateUndoService extends AbstractContentUndoService2D<
  ImageContentState,
  ImageUndoState
> {
  constructor(private rootStore: ImageContentStore) {
    super(rootStore);
  }

  protected override hasTrackedChanges(
    before: ImageUndoState | undefined,
    after: ImageUndoState,
  ) {
    return before?.rawMask !== after.rawMask;
  }

  protected override extractUndoState(
    state: ImageContentState,
  ): ImageUndoState {
    return pick(state, 'rawMask', 'selection', 'viewBox');
  }

  protected override applyUndoState(
    stateToApply: ImageUndoState,
    undoOrRedo: 'undo' | 'redo',
    stateToOverwrite: ImageUndoState,
  ) {
    super.applyUndoState(stateToApply, undoOrRedo, stateToOverwrite);

    this.rootStore.batch(() => {
      this.store('rawMask').set(stateToApply.rawMask);
    });

    if (stateToApply.rawMask) {
      // Some annotation state will use more memory for undo so to avoid using
      // too much memory to store undo states, the max depth will depend on the
      // size of the memory required.
      this.maxDepth = Math.max(2, maxMemory / stateToApply.rawMask.length);
    }
  }
}
